import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { TranslationNS } from '@gokrew/models/constants';
import type { ReportingPerson } from '@gokrew/models/reporting';
import { applyReportingLines, assignTemporaryGroup } from '@/api/reporting';
import { useAuth } from '@/context/AuthContext';
import { usePermissions } from '@/context/PermissionContext';
import { useHandoverExceptions } from '@/hooks/handovers/useHandovers';
import { permissionKeys } from '@/utility';
import {
  buildManageTeamPlan,
  toBulkItems,
  toOrphanTeams,
  type ManageTeamData,
  type ManageTeamPlan,
  type ManageTeamSelection,
} from '@/utils/manageTeam';
import {
  invalidateReporting,
  useOrgChart,
  useTemporaryGroups,
} from './useReporting';

interface UseManageTeamPlanInput {
  /** Load the data (the toggle is on, or the dialog is open). */
  enabled: boolean;
  /** The person taking the team; null for a new hire not saved yet. */
  managerId: string | null;
  selection: ManageTeamSelection;
  /** New hire only: their own manager ("Reports to"). */
  reportsTo?: ReportingPerson | null;
}

/**
 * Everything the "Who will report to {name}?" block shows (waiting temporary
 * groups, teams left without a manager, the current structure) and the plan
 * the selection turns into. Reads need `reporting.read`; without it only the
 * people picker is offered.
 */
export const useManageTeamPlan = ({
  enabled,
  managerId,
  selection,
  reportsTo = null,
}: UseManageTeamPlanInput) => {
  const { permissions } = usePermissions();
  const canRead = permissions.has(permissionKeys.reporting.read);
  const load = enabled && canRead;
  const groupsQuery = useTemporaryGroups(load);
  const orgChartQuery = useOrgChart(load);
  const exceptionsQuery = useHandoverExceptions(load);

  const data = useMemo<ManageTeamData>(() => {
    const exceptions = exceptionsQuery.data;
    const departing: Record<string, string | null> = {};
    for (const handover of exceptions?.handovers ?? []) {
      departing[handover.id] = handover.departing.name;
    }
    return {
      // A group the person already covers is kept with them through "Keep
      // with" on the Exceptions page, not moved to them.
      groups: (groupsQuery.data ?? []).filter(
        (group) =>
          group.members.length > 0 &&
          group.coveringManager.employeeId !== managerId
      ),
      orphanTeams: toOrphanTeams(
        (exceptions?.employeesWithoutManager ?? []).filter(
          (row) => row.employeeId !== managerId
        ),
        departing
      ),
      orgChart: orgChartQuery.data ?? [],
    };
  }, [groupsQuery.data, orgChartQuery.data, exceptionsQuery.data, managerId]);

  const plan = useMemo(
    () => buildManageTeamPlan({ selection, data, managerId, reportsTo }),
    [selection, data, managerId, reportsTo]
  );

  return {
    data,
    plan,
    canRead,
    isLoading:
      load &&
      (groupsQuery.isLoading ||
        orgChartQuery.isLoading ||
        exceptionsQuery.isLoading),
  };
};

export interface ManageTeamResult {
  /** People now reporting to the person (already there included). */
  moved: number;
  /** Names of the people who could not be moved. */
  notMoved: string[];
  /** The person's own line could not be set. */
  ownLineFailed: boolean;
}

/**
 * Applies a plan: the bulk moves first (the person's own line leads, see
 * `toBulkItems`), then each temporary group through the assign endpoint so
 * the interim lines end as `interim_ended`. Never throws: one toast says how
 * many now report to the person and names whoever could not be moved,
 * pointing HR to the person's Manager section.
 */
export const useApplyManageTeam = () => {
  const { companyId } = useAuth();
  const qc = useQueryClient();
  const { t } = useTranslation(TranslationNS.Employee);

  const report = useCallback(
    (plan: ManageTeamPlan, result: ManageTeamResult, name: string) => {
      if (result.ownLineFailed && plan.ownLine) {
        toast.error(
          plan.ownLine.managerName
            ? t('reporting_line.manages_team.toast.own_line_failed', {
                name,
                manager: plan.ownLine.managerName,
              })
            : t('reporting_line.manages_team.toast.own_line_cleared_failed', {
                name,
              })
        );
      }
      if (result.notMoved.length) {
        toast.error(
          t('reporting_line.manages_team.toast.not_moved', {
            names: result.notMoved.join(', '),
            name,
          }),
          { duration: 10000 }
        );
      }
      if (result.moved) {
        toast.success(
          t('reporting_line.manages_team.toast.done', {
            count: result.moved,
            name,
          })
        );
      }
    },
    [t]
  );

  return useCallback(
    async (
      plan: ManageTeamPlan,
      managerEmployeeId: string,
      managerName: string
    ): Promise<ManageTeamResult> => {
      const result: ManageTeamResult = {
        moved: 0,
        notMoved: [],
        ownLineFailed: false,
      };
      if (!companyId) return result;
      const nameOf = (id: string) => plan.names[id] ?? id;

      const items = toBulkItems(plan, managerEmployeeId);
      if (items.length) {
        try {
          const res = await applyReportingLines(companyId, {
            source: 'import',
            items,
          });
          for (const row of res.data?.results ?? []) {
            const isSelf = row.employeeId === managerEmployeeId;
            if (row.status === 'error') {
              if (isSelf) result.ownLineFailed = true;
              else result.notMoved.push(nameOf(row.employeeId));
            } else if (!isSelf) {
              result.moved += 1;
            }
          }
        } catch {
          // The request's own toast explains why; nobody in it moved.
          if (plan.ownLine) result.ownLineFailed = true;
          result.notMoved.push(...plan.moves.map(nameOf));
        }
      }

      for (const { group, employeeIds } of plan.groups) {
        try {
          const res = await assignTemporaryGroup(companyId, {
            handoverId: group.handoverId,
            coveringManagerEmployeeId: group.coveringManager.employeeId,
            newManagerEmployeeId: managerEmployeeId,
            employeeIds,
          });
          result.moved += res.data?.moved ?? employeeIds.length;
        } catch {
          // A group that changed meanwhile (409) is refused as a whole.
          result.notMoved.push(...employeeIds.map(nameOf));
        }
      }

      invalidateReporting(qc, companyId);
      report(plan, result, managerName);
      return result;
    },
    [companyId, qc, report]
  );
};
